<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";
import Button from "@/design/primitives/Button.vue";
import Icon from "@/design/primitives/Icon.vue";
import Menu from "@/design/primitives/Menu.vue";
import Tooltip from "@/design/primitives/Tooltip.vue";
import { useVoiceRecorder } from "@/shared/audio/use-voice-recorder";
import {
	type AttachFailure,
	acceptOf,
	filesFrom,
	isFailure,
	labelsOf,
	type PendingAudio,
	type PendingFile,
	type PendingImage,
	type ReadableFormats,
	readableFormats,
	routeOf,
	toPendingAudio,
	toPendingImage,
	uploadPendingFile,
} from "../api/attachments";
import { useAgentCapabilities } from "../api/use-agents";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * Onde se escreve para o agente, com o que vai junto.
 *
 * Saiu de `StreamingChat.vue` inteiro e sem mudar de comportamento: o anexo, o
 * colar, o arrastar e o Enter-envia continuam sendo as mesmas quatro portas para
 * a mesma caixa. Visão é entrada NATIVA do modelo (ADR-007) — a foto de um
 * formulário, o print de um erro ou uma tabela impressa entram direto, e nada
 * disso sobrevive a ser redigitado.
 */
const props = defineProps<{
	streaming: boolean;
	agentCode?: string;
	/** Falha do turno. A falha de anexo é daqui e não sobe para ninguém. */
	error?: string;
}>();

const emit = defineEmits<{
	submit: [
		payload: { text: string; images: PendingImage[]; audio?: PendingAudio; files?: PendingFile[] },
	];
	stop: [];
	/**
	 * JUNTAR: a próxima pergunta, escrita enquanto a atual ainda corre.
	 *
	 * A V2 tem os dois botões durante o turno — "Parar" e "Juntar" — e a diferença
	 * entre eles é o que se faz com o que já está sendo escrito. Sem "Juntar", quem
	 * lembra de uma segunda coisa no meio de um turno de dois minutos tem três
	 * opções ruins: interromper o que está correndo, esperar olhando, ou escrever e
	 * torcer para não apertar Enter. Quem guarda a pergunta é `StreamingChat`, que
	 * é quem sabe quando o turno acabou.
	 */
	queue: [
		payload: { text: string; images: PendingImage[]; audio?: PendingAudio; files?: PendingFile[] },
	];
}>();

const { t } = useAgentsI18n();

const draft = ref("");
const filePicker = ref<HTMLInputElement | null>(null);
const capabilities = useAgentCapabilities(computed(() => props.agentCode ?? ""));
const resourceItems = computed(() => {
	const tools = new Set((capabilities.data.value?.tools ?? []).map((tool) => tool.name));
	return [
		{ value: "attach", label: t("chat.attach") },
		...["files", "media", "web"]
			.filter((tool) => tools.has(tool))
			.map((tool) => ({ value: tool, label: t(`resources.${tool}`) })),
	];
});
function resource(value: string): void {
	if (value === "attach") {
		filePicker.value?.click();
		return;
	}
	prefill(t(`resources.${value}Request`), false, true);
}

const pending = ref<PendingImage[]>([]);
const dragging = ref(false);
const attachError = ref("");
/**
 * O que não é foto — vídeo, áudio, PDF, planilha —, já guardado no servidor e à
 * espera do envio. `uploading` conta os que ainda sobem: enviar antes seria
 * mandar a pergunta sem o anexo.
 */
const pendingFiles = ref<PendingFile[]>([]);
const uploading = ref(0);
/** A lista do servidor (`GET /v3/agents/attachments`) — a única. */
const formats = ref<ReadableFormats | null>(null);
void readableFormats()
	.then((value) => {
		formats.value = value;
	})
	.catch(() => undefined);
const box = ref<HTMLTextAreaElement | null>(null);

/**
 * A voz, como quarta porta da caixa: gravar → chip pendente → enviar junto.
 *
 * UMA nota por turno, de propósito — a voz aqui é ditado (o servidor a
 * transcreve e descarta os bytes), e duas notas numa pergunta são duas
 * perguntas. Gravar de novo substitui a pendente.
 */
const pendingAudio = ref<PendingAudio | null>(null);
const recorder = useVoiceRecorder({
	onRecorded: (file) => {
		void toPendingAudio(file, recorder.seconds.value).then((audio) => {
			// Gravar de novo substitui a pendente — e a URL da substituída morre já,
			// pela mesma regra dos anexos de imagem.
			if (pendingAudio.value) URL.revokeObjectURL(pendingAudio.value.previewUrl);
			pendingAudio.value = audio;
		});
	},
	onFail: () => {
		attachError.value = t("chat.noMicrophone");
	},
});

function removeAudio(): void {
	if (pendingAudio.value) URL.revokeObjectURL(pendingAudio.value.previewUrl);
	pendingAudio.value = null;
}
const { recording, seconds } = recorder;

const clock = computed(() => {
	const m = Math.floor(seconds.value / 60);
	const s = String(seconds.value % 60).padStart(2, "0");
	return `${m}:${s}`;
});

const canSend = computed(
	() =>
		(draft.value.trim().length > 0 ||
			pending.value.length > 0 ||
			pendingFiles.value.length > 0 ||
			pendingAudio.value !== null) &&
		!props.streaming &&
		!recording.value &&
		uploading.value === 0,
);

async function attach(files: readonly File[]): Promise<void> {
	const known = formats.value ?? (await readableFormats().catch(() => null));
	if (!known) {
		attachError.value = t("attach.uploadFailed", { name: files[0]?.name ?? "" });
		return;
	}
	for (const file of files) {
		const route = routeOf(known, file);
		// The refusal names the FILE — and what IS readable, from the server's list:
		// a refusal that only says "no" leaves the person guessing what to send.
		if (isFailure(route)) {
			refuse(route, file.name, known);
			continue;
		}
		if (route === "image") {
			pending.value = [...pending.value, await toPendingImage(file)];
			continue;
		}
		if (pendingFiles.value.length + uploading.value >= known.maxFiles) {
			attachError.value = t("attach.tooMany", { max: known.maxFiles });
			continue;
		}
		uploading.value++;
		try {
			const stored = await uploadPendingFile(file);
			if (isFailure(stored)) refuse(stored, file.name, known);
			else pendingFiles.value = [...pendingFiles.value, stored];
		} finally {
			uploading.value--;
		}
	}
}

function refuse(failure: AttachFailure, name: string, known: ReadableFormats): void {
	attachError.value = t(`attach.${failure.reason}`, {
		name,
		formats: labelsOf(known),
		max: Math.round(known.maxBytes / 1024 / 1024),
	});
}

function unattachFile(index: number): void {
	pendingFiles.value = pendingFiles.value.filter((_, i) => i !== index);
}

function unattach(index: number): void {
	const image = pending.value[index];
	// Revogado explicitamente: um object URL vive até o documento morrer, e uma
	// sessão longa de anexar-e-remover vaza todos eles.
	if (image) URL.revokeObjectURL(image.previewUrl);
	pending.value = pending.value.filter((_, i) => i !== index);
}

function onPaste(event: ClipboardEvent): void {
	const files = filesFrom(event.clipboardData);
	// Só impede o padrão quando HÁ arquivo: engolir um colar de texto comum seria
	// enlouquecedor.
	if (files.length === 0) return;
	event.preventDefault();
	void attach(files);
}

function onDrop(event: DragEvent): void {
	dragging.value = false;
	const files = filesFrom(event.dataTransfer);
	if (files.length > 0) void attach(files);
}

async function pick(event: Event): Promise<void> {
	const input = event.target as HTMLInputElement;
	await attach(Array.from(input.files ?? []));
	// Limpo para que escolher o MESMO arquivo duas vezes ainda dispare o evento.
	input.value = "";
}

/**
 * Cresce com o conteúdo, até um teto.
 *
 * Uma caixa de uma linha para um parágrafo é o atrito pequeno que faz a pessoa
 * escrever menos do que queria. O `auto` antes é o que permite ENCOLHER de volta.
 */
function resize(): void {
	const el = box.value;
	if (!el) return;
	el.style.height = "auto";
	el.style.height = `${Math.min(el.scrollHeight + 2, 240)}px`;
}

watch(draft, resize, { flush: "post" });
let previousWidth = 0;
useResizeObserver(box, ([entry]) => {
	if (entry && entry.contentRect.width !== previousWidth) {
		previousWidth = entry.contentRect.width;
		resize();
	}
});

function onKeydown(event: KeyboardEvent): void {
	if (
		event.key !== "Enter" ||
		event.shiftKey ||
		event.ctrlKey ||
		event.altKey ||
		event.metaKey ||
		event.isComposing ||
		event.keyCode === 229
	)
		return;
	event.preventDefault();
	onEnter();
}

/**
 * O Enter envia — e, durante um turno, ENFILEIRA.
 *
 * A tecla nunca para o turno: parar de propósito é um clique, e parar sem querer é
 * o que aconteceria aqui. Mas engolir o Enter durante o turno também tem custo —
 * quem escreveu a frase inteira espera que ela vá para algum lugar. Ela vai para a
 * fila, que é o que "Juntar" faz com o mesmo texto.
 */
function onEnter(): void {
	if (recording.value) return;
	if (props.streaming) void queueNext();
	else void submit();
}

/** O que está escrito, e a caixa limpa. Um só lugar, para o enviar e o enfileirar
 *  nunca discordarem sobre o que é "o que está escrito". */
function takeDraft(): {
	text: string;
	images: PendingImage[];
	audio?: PendingAudio;
	files?: PendingFile[];
} | null {
	if (
		draft.value.trim().length === 0 &&
		pending.value.length === 0 &&
		pendingFiles.value.length === 0 &&
		!pendingAudio.value
	) {
		return null;
	}
	// Um anexo ainda a subir não viaja: sairia a pergunta sem ele.
	if (uploading.value > 0) return null;
	const payload = {
		text: draft.value.trim(),
		images: pending.value,
		...(pendingAudio.value ? { audio: pendingAudio.value } : {}),
		...(pendingFiles.value.length ? { files: pendingFiles.value } : {}),
	};
	draft.value = "";
	pending.value = [];
	pendingFiles.value = [];
	pendingAudio.value = null;
	attachError.value = "";
	return payload;
}

/** Adds another message without interrupting the current tool. */
async function queueNext(): Promise<void> {
	if (recording.value) return;
	const payload = takeDraft();
	if (!payload) return;
	await nextTick();
	box.value?.focus();
	emit("queue", payload);
}

async function submit(): Promise<void> {
	if (props.streaming) {
		emit("stop");
		return;
	}
	if (!canSend.value) return;
	const payload = takeDraft();
	if (!payload) return;
	await nextTick();
	box.value?.focus();
	emit("submit", payload);
}

/**
 * Pré-preencher, sem enviar — o click-to-edit do construtor de site escreve
 * "Nesta seção (…): " aqui e devolve o cursor à pessoa. Exposto porque o dono
 * do texto é este componente; um prop viraria duas fontes para o mesmo campo.
 */
function prefill(text: string, send = false, append = false): void {
	draft.value = append && draft.value.trim() ? `${draft.value}\n\n${text}` : text;
	// Enviar direto só com a doca livre: com um turno a correr, `submit` PARARIA o
	// turno — aí a frase fica escrita e a pessoa decide.
	if (send && !props.streaming) {
		void submit();
		return;
	}
	void nextTick(() => {
		resize();
		box.value?.focus();
	});
}

defineExpose({ prefill });
</script>

<template>
	<form
		class="shrink-0 bg-surface px-3 pt-1 pb-3"
		@submit.prevent="onEnter"
		@dragover.prevent="dragging = true"
		@dragleave="dragging = false"
		@drop.prevent="onDrop"
	>
		<div class="rounded-2xl border border-border bg-surface-raised p-2 shadow-sm transition-colors focus-within:border-border-strong" :class="dragging ? 'border-accent bg-accent-subtle' : ''">
			<!-- A nota de voz gravada, esperando o envio — removível como uma imagem. -->
			<div v-if="pendingAudio" class="mb-2">
				<span
					class="inline-flex items-center gap-2 rounded-md border border-border bg-bg-subtle px-2 py-1 text-xs text-fg-muted"
				>
					<Icon name="mic" class="size-3.5" />
					<!-- Tocável antes de enviar: conferir o que se gravou é o gesto que
					     evita mandar o vizinho latindo no fundo. -->
					<audio :src="pendingAudio.previewUrl" controls class="h-8 w-48" />
					<button
						type="button"
						class="text-fg-subtle hover:text-fg"
						:aria-label="t('chat.removeAudio')"
						@click="removeAudio"
					>
						×
					</button>
				</span>
			</div>

			<!-- Anexado e ainda não enviado. Acima da caixa, e não dentro dela, para um
			     nome de arquivo comprido não espremer o lugar onde se escreve. -->
			<div v-if="pending.length" class="mb-2 flex flex-wrap gap-2">
				<span v-for="(image, i) in pending" :key="i" class="relative">
					<img
						:src="image.previewUrl"
						:alt="image.name"
						class="size-16 rounded-md border border-border object-cover"
					/>
					<button
						type="button"
						class="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border border-border bg-surface text-xs text-fg-muted shadow"
						:aria-label="t('chat.removeAttached', { name: image.name })"
						@click="unattach(i)"
					>
						×
					</button>
				</span>
			</div>

			<!-- Vídeo, áudio, PDF, planilha: um cartão com o nome, enquanto sobe e depois. -->
			<div v-if="pendingFiles.length || uploading" class="mb-2 flex flex-wrap gap-2">
				<span
					v-for="(file, i) in pendingFiles"
					:key="file.id"
					class="inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-bg-subtle px-2 py-1 text-xs text-fg-muted"
				>
					<Icon name="paperclip" class="size-3.5 shrink-0" />
					<span class="truncate">{{ file.name }}</span>
					<button
						type="button"
						class="text-fg-subtle hover:text-fg"
						:aria-label="t('chat.removeAttached', { name: file.name })"
						@click="unattachFile(i)"
					>
						×
					</button>
				</span>
				<span v-if="uploading" class="inline-flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs text-fg-subtle" aria-live="polite">
					<span class="inline-block size-1.5 animate-pulse rounded-full bg-accent" aria-hidden="true" />
					{{ t("attach.uploading") }}
				</span>
			</div>

			<textarea
				ref="box"
				v-model="draft"
				rows="1"
				:placeholder="t('chat.composePlaceholder')"
				:aria-label="t('chat.composePlaceholder')"
				class="block max-h-[min(240px,35dvh)] min-h-11 w-full resize-none overflow-y-auto border-0 bg-transparent px-2 py-2 text-base leading-6 text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-0 sm:text-sm"
				@paste="onPaste"
				@keydown="onKeydown"
			/>
			<div class="mt-1 flex flex-wrap items-center gap-1.5">
				<input ref="filePicker" type="file" :accept="formats ? acceptOf(formats) : undefined" multiple class="sr-only" :aria-label="t('chat.attach')" @change="pick" />
 <Menu :items="resourceItems" :label="t('resources.heading')" side="top" @select="resource">
 <template #trigger><button type="button" class="flex size-10 shrink-0 items-center justify-center rounded-full text-fg-muted hover:bg-bg-muted" :aria-label="t('resources.heading')"><Icon name="plus" class="size-5" /></button></template>
 </Menu>

				<!-- Gravando: o relógio no lugar do microfone, com desistir e concluir.
				     Fora disso, o microfone mora ao lado do enviar, como no app do Claude. -->
				<template v-if="recording">
					<span class="flex h-9 items-center gap-1.5 rounded-md border border-danger/40 bg-danger-subtle px-2 text-xs text-danger">
						<span class="inline-block size-1.5 animate-pulse rounded-full bg-danger" aria-hidden="true" />
						<span class="tabular-nums">{{ clock }}</span>
					</span>
					<button
						type="button"
						class="flex size-10 shrink-0 items-center justify-center rounded-full text-fg-muted hover:bg-bg-muted"
						:aria-label="t('chat.cancelRecording')"
						@click="recorder.cancel()"
					>
						×
					</button>
					<button
						type="button"
						class="flex size-9 shrink-0 items-center justify-center rounded-md border border-accent-border bg-accent-subtle text-accent hover:bg-accent-subtle/80"
						:aria-label="t('chat.stopRecording')"
						@click="recorder.stop()"
					>
						<Icon name="check" />
					</button>
				</template>
				<!-- Durante o turno são DOIS botões, como na V2: parar o que corre, ou
				     mandar a próxima junto. "Juntar" só existe quando há o que juntar. -->
				<div class="ml-auto flex items-center gap-2">
					<Tooltip v-if="recorder.supported && !recording" :text="t('chat.record')">
						<button
							type="button"
							class="flex size-10 shrink-0 items-center justify-center rounded-full text-fg-muted hover:bg-bg-muted"
							:aria-label="t('chat.record')"
							@click="recorder.start()"
						>
							<Icon name="mic" class="size-5" />
						</button>
					</Tooltip>
				<template v-if="streaming">
					<Button
						v-if="draft.trim() || pending.length || pendingFiles.length || pendingAudio"
						size="md" pill :disabled="recording"
						@click="queueNext"
					>
						{{ t("chat.queue") }}
					</Button>
					<Button type="button" size="md" pill variant="secondary" @click="emit('stop')"><span aria-hidden="true" class="size-3 rounded-sm bg-current" />{{ t("chat.stop") }}</Button>
				</template>
				<Button v-else type="submit" size="md" pill class="size-11 p-0" :disabled="!canSend" :aria-label="$t('action.send')" :title="$t('action.send')">
					<Icon name="arrowUp" class="size-5" />
				</Button>
				</div>
			</div>
		</div>
		<p v-if="error || attachError" role="alert" class="mt-1 text-xs text-danger">{{ error || attachError }}</p>
	</form>
</template>
